import { useState } from 'react';
import { memberColor, t } from '../theme';
import { useStore } from '../store';

/**
 * First run. Shown whenever `setupComplete` is false, and re-openable from
 * Einstellungen.
 *
 * Two steps, both of which earn their place: members, because every colour in
 * the calendar depends on them; and the time zone, because *all* expiry maths
 * is done against it server-side and a VPS defaults to UTC — getting it wrong
 * silently shifts every "läuft heute ab" by a day.
 */
const COMMON_ZONES = ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich', 'Europe/London', 'UTC'];

export function Setup() {
  const members = useStore((s) => s.members);
  const settings = useStore((s) => s.settings);
  const { addMember, renameMember, removeMember, patchSettings, flash } = useStore.getState();

  const detected = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const [step, setStep] = useState<1 | 2>(1);
  const [zone, setZone] = useState(settings.timeZone || detected);
  const [saving, setSaving] = useState(false);

  const zones = COMMON_ZONES.includes(detected) ? COMMON_ZONES : [detected, ...COMMON_ZONES];

  const finish = async () => {
    if (saving) return;
    setSaving(true);
    try {
      await patchSettings({ timeZone: zone, setupComplete: true });
      flash('Einrichtung abgeschlossen');
    } catch {
      flash('Konnte nicht gespeichert werden');
    } finally {
      setSaving(false);
    }
  };

  const primary = (label: string, onTap: () => void, disabled = false) => (
    <div
      className={disabled ? undefined : 'press'}
      onClick={() => !disabled && onTap()}
      style={{
        height: 86,
        padding: '0 54px',
        borderRadius: 22,
        background: t.accent,
        color: t.accentInk,
        display: 'flex',
        alignItems: 'center',
        fontSize: 26,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        opacity: disabled ? 0.35 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {label}
    </div>
  );

  return (
    <div
      style={{
        flex: 1,
        minWidth: 0,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 60px',
        gap: 24,
      }}
    >
      <div style={{ fontFamily: t.mono, fontSize: 13, letterSpacing: '0.16em', color: t.inkFaint }}>
        FRISK · ERSTEINRICHTUNG · SCHRITT {step} VON 2
      </div>

      {step === 1 ? (
        <>
          <div style={{ fontSize: 54, fontWeight: 500, letterSpacing: '-0.045em', textAlign: 'center', lineHeight: 1.1 }}>
            Willkommen bei frisk<span style={{ color: t.accent }}>.</span>
          </div>
          <div style={{ fontSize: 21, color: t.inkMuted, maxWidth: 560, textAlign: 'center', textWrap: 'pretty' }}>
            Wer wohnt hier? Jedes Mitglied bekommt eine Farbe — im Kalender und beim Einräumen erkennbar.
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', maxWidth: 780, marginTop: 8 }}>
            {members.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '14px 16px 14px 24px',
                  borderRadius: 999,
                  background: 'oklch(0.28 0.012 60)',
                  border: `1px solid ${memberColor(m.hue)}`,
                  fontSize: 22,
                  fontWeight: 500,
                }}
              >
                <div style={{ width: 14, height: 14, borderRadius: 999, background: memberColor(m.hue) }} />
                <span className="press" onClick={() => renameMember(m.id, m.name)}>
                  {m.name}
                </span>
                <div
                  className="press"
                  onClick={() => void removeMember(m.id)}
                  style={{
                    width: 34,
                    height: 34,
                    borderRadius: 999,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 20,
                    color: 'oklch(0.66 0.04 30)',
                  }}
                >
                  ×
                </div>
              </div>
            ))}

            <div
              className="press"
              onClick={addMember}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '16px 24px',
                borderRadius: 999,
                border: '1px dashed oklch(1 0 0 / 0.2)',
                fontSize: 22,
                fontWeight: 500,
                color: 'oklch(0.82 0.015 70)',
              }}
            >
              <span style={{ color: t.accent, fontSize: 24 }}>+</span>Mitglied
            </div>
          </div>

          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 16 }}>
            {primary('Weiter', () => setStep(2), members.length === 0)}
          </div>
          {members.length === 0 && (
            <div style={{ fontSize: 18, color: t.inkMuted }}>Mindestens ein Mitglied anlegen.</div>
          )}
        </>
      ) : (
        <>
          <div style={{ fontSize: 48, fontWeight: 500, letterSpacing: '-0.045em', textAlign: 'center', lineHeight: 1.1 }}>
            Wo steht der Kühlschrank?
          </div>
          <div style={{ fontSize: 21, color: t.inkMuted, maxWidth: 620, textAlign: 'center', textWrap: 'pretty' }}>
            Alle Ablaufdaten werden gegen diese Zeitzone gerechnet. Der Server läuft sonst nach UTC — dann
            stimmt „läuft heute ab" um einen Tag nicht.
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, justifyContent: 'center', maxWidth: 780, marginTop: 8 }}>
            {zones.map((z) => {
              const on = zone === z;
              return (
                <div
                  key={z}
                  className="press"
                  onClick={() => setZone(z)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '16px 26px',
                    borderRadius: 999,
                    background: on ? 'oklch(0.31 0.012 60)' : 'oklch(0.26 0.012 60)',
                    border: `1px solid ${on ? t.accent : 'oklch(1 0 0 / 0.06)'}`,
                    fontSize: 21,
                    fontWeight: 500,
                  }}
                >
                  {z}
                  {z === detected && (
                    <span style={{ fontSize: 16, color: t.inkMuted }}>erkannt</span>
                  )}
                </div>
              );
            })}
          </div>

          <div style={{ marginTop: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
            <div
              className="press"
              onClick={() => setStep(1)}
              style={{
                height: 86,
                padding: '0 34px',
                borderRadius: 22,
                border: t.hairlineStrong,
                display: 'flex',
                alignItems: 'center',
                fontSize: 22,
                fontWeight: 500,
                color: 'oklch(0.8 0.015 70)',
              }}
            >
              Zurück
            </div>
            {primary("Los geht's", () => void finish(), saving)}
          </div>
        </>
      )}
    </div>
  );
}
